// Commercial — Tableau détaillé par visiteur AMO, lignes expandables.
import { useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useCommercialFetch, FUNNEL_COLORS } from "./useCommercialFetch";

function RateBadge({ value }) {
  const color =
    value >= 80 ? "var(--oh-stat-green-text)"
    : value >= 50 ? "var(--oh-stat-amber-text)"
    : value >= 20 ? "var(--oh-stat-orange-text)"
    : "var(--oh-stat-red-text)";
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold"
      style={{ background: `${color}20`, color }}>
      {value}%
    </span>
  );
}

function VisiteurRow({ v, expanded, onToggle }) {
  return (
    <>
      <tr className="border-t cursor-pointer transition-colors duration-150"
        style={{ borderColor: "var(--oh-border)", background: expanded ? "var(--oh-bg)" : "transparent" }}
        onClick={onToggle}>
        <td className="px-4 py-3">
          {expanded
            ? <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
            : <ChevronRight className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />}
        </td>
        <td className="px-4 py-3 font-semibold">{v.visiteur}</td>
        <td className="text-center px-3 py-3">
          <Badge variant="outline" className="font-mono text-xs" style={{ borderColor: "var(--oh-border)" }}>
            {v.total}
          </Badge>
        </td>
        <td className="text-center px-3 py-3">
          <span className="font-mono text-xs">{v.contrats_signes}</span>
          <span className="text-xs ml-1" style={{ color: "var(--oh-text-muted)" }}>({v.taux_contrat}%)</span>
        </td>
        <td className="text-center px-3 py-3"><RateBadge value={v.taux_depot} /></td>
        <td className="text-center px-3 py-3"><RateBadge value={v.taux_octroi} /></td>
        <td className="text-center px-3 py-3"><RateBadge value={v.taux_cloture} /></td>
        <td className="text-right px-4 py-3 font-mono text-xs">
          {(v.ca_amo || 0).toLocaleString("fr-FR")} EUR
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: "var(--oh-bg)" }}>
          <td colSpan={8} className="px-8 py-4">
            <div className="space-y-2">
              <p className="text-xs font-medium mb-3" style={{ color: "var(--oh-text-muted)" }}>
                Entonnoir détaillé — {v.visiteur}
              </p>
              {(v.funnel || []).map((step, i) => {
                const maxCount = v.funnel[0]?.count || 1;
                const widthPct = Math.max((step.count / maxCount) * 100, 6);
                return (
                  <div key={step.key} className="flex items-center gap-3">
                    <span className="text-xs w-24 text-right shrink-0" style={{ color: "var(--oh-text-muted)" }}>
                      {step.label}
                    </span>
                    <div className="flex-1 relative h-6">
                      <div className="h-full rounded-r flex items-center px-2"
                        style={{
                          width: `${widthPct}%`,
                          background: FUNNEL_COLORS[i] || "var(--oh-stat-blue-text)",
                          opacity: 0.85, minWidth: "40px",
                        }}>
                        <span className="text-[10px] font-bold text-white whitespace-nowrap">
                          {step.count} ({step.pct}%)
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CommercialVisiteursDetailWidget() {
  const { data, loading, error } = useCommercialFetch();
  const [expanded, setExpanded] = useState(null);

  if (loading) {
    return (
      <div className="rounded-xl p-5 h-full flex items-center justify-center"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-xl p-5"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}>
        Erreur Détail visiteurs : {error || "—"}
      </div>
    );
  }

  const items = data.par_visiteur || [];

  return (
    <div className="rounded-xl overflow-hidden h-full"
      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      data-testid="commercial-visiteurs-detail-widget">
      <div className="p-4 border-b" style={{ borderColor: "var(--oh-border)" }}>
        <h3 className="text-base font-semibold" style={{ color: "var(--oh-text)" }}>
          Détail par visiteur AMO
        </h3>
      </div>
      <div className="overflow-x-auto oh-responsive-table">
        <table className="w-full min-w-[900px] text-sm" style={{ color: "var(--oh-text)" }}>
          <thead>
            <tr className="text-xs uppercase"
              style={{ background: "var(--oh-bg)", color: "var(--oh-text-muted)" }}>
              <th className="text-left px-4 py-3 font-medium"></th>
              <th className="text-left px-4 py-3 font-medium">Visiteur</th>
              <th className="text-center px-3 py-3 font-medium">Dossiers</th>
              <th className="text-center px-3 py-3 font-medium">Contrats</th>
              <th className="text-center px-3 py-3 font-medium">Dépôt</th>
              <th className="text-center px-3 py-3 font-medium">Octroi</th>
              <th className="text-center px-3 py-3 font-medium">Clôture</th>
              <th className="text-right px-4 py-3 font-medium">CA AMO</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <VisiteurRow
                key={v.visiteur} v={v}
                expanded={expanded === v.visiteur}
                onToggle={() => setExpanded(expanded === v.visiteur ? null : v.visiteur)}
              />
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="text-center py-8 text-xs" style={{ color: "var(--oh-text-muted)" }}>
            Aucun visiteur AMO.
          </div>
        )}
      </div>
    </div>
  );
}
